//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//

import React from "react";
import {makeStyles, withStyles} from "@material-ui/core/styles";
import {ListBox} from "@grapecity/wijmo.react.input";
import "@grapecity/wijmo.styles/wijmo.css";
import "src/components/auditor/wijmo.css";
import * as Config from "eai-configurator-ui/components/configuration/utils/ConfigurationRegistry";
import AuditorReportTable from "src/components/auditor/table/AuditorReportTable";
import ConfigurationSwitch from "eai-configurator-ui/components/configuration/base/ConfigurationSwitch";
import ConfigurationAsyncComboBox from "eai-configurator-ui/components/configuration/base/ConfigurationAsyncComboBox";
import MessageDialog from "eai-configurator-ui/components/configuration/dialog/MessageDialog";
import Badge from "@material-ui/core/Badge";
import SettingsIcon from "@material-ui/icons/Settings";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccordionDetails from "@material-ui/core/ExpansionPanelDetails";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import * as Utils from "eai-configurator-ui/components/Utils";

const useStyles = makeStyles((theme) => ({
  button: {
    margin: "3px",
  },
  grid: {
    position: "relative",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#00A994",
    position: "absolute",
    backgroundColor: "rgba(255,255, 255, 0.5)",
  },
  columnPickerIcon: {
    cursor: "pointer",
    color: "#FF8754",
    margin: "3px",
  },
  columnPickerDiv: {
    display: "none",
  },
  columnPicker: {
    /* IE fallback */
    padding: "12px",
    marginLeft: "12px",
    marginTop: "26px",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)",
  },
  accordion: {marginTop: -6},
  expandablePanel: {margin: 0},
  expandablePanelIcon: {},
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    left: -3,
    top: 18,
    padding: "0 4px",
  },
}))(Badge);

const AuditorEventTablePage = ({path, appSettings, selectedEntity, navigateToResults}) => {
  const columnPicker = React.useRef(null);

  //
  const [ignoredFieldsDialogOpen, setIgnoredFieldsDialogOpen] = React.useState(false);
  const [undoDialogOpen, setUndoDialogOpen] = React.useState(false);
  const [undoUndeleteActive, setUndoUndeleteActive] = React.useState(false);
  const [undoingChanges, setUndoingChanges] = React.useState(false);
  const [undeletingChanges, setUndeletingChanges] = React.useState(false);

  React.useEffect(() => {
    setUndoUndeleteActive(undoingChanges || undeletingChanges);
  }, [undoingChanges, undeletingChanges])

  //
  const [ignoredFieldsFilterEnabled, setIgnoredFieldsFilterEnabled,] = React.useState(
    Config.fromPath(path + ":ignoredFieldsFilterEnabled", true).data.value
  );

  //
  const [incrementalDataRetrieval, setIncrementalDataRetrieval,] = React.useState(
    Config.fromPath(path + ":incrementalDataRetrieval", true).data.value
  );

  //
  const [loadCustomFields, setLoadCustomFields,] = React.useState(
    Config.fromPath(path + ":loadCustomFields", true).data.value
  );

  //
  const [loadCustomFieldsColumns, setLoadCustomFieldsColumns,] = React.useState(
    Config.fromPath(path + ":loadCustomFieldsColumns", true).data.value
  );

  //
  const [refreshAutomatically, setRefreshAutomatically,] = React.useState(
    Config.fromPath(path + ":refreshAutomatically", true).data.value
  );

  //
  const [resizeAutomatically, setResizeAutomatically,] = React.useState(
    Config.fromPath(path + ":resizeColumnsAutomatically", true).data.value
  );

  const handleIncrementalDataRetrievalChanged = (value) => {
    setIncrementalDataRetrieval(() => value === true || value === "true");
    Config.setDataValue(path + ":incrementalDataRetrieval", value === true || value === "true");
  };

  const handleLoadCustomFieldsChanged = (value) => {
    setLoadCustomFields(() => value === true || value === "true");
    Config.setDataValue(path + ":loadCustomFields", value === true || value === "true");
  };

  const handleLoadCustomFieldsColumnsChanged = (value) => {
    setLoadCustomFieldsColumns(() => value === true || value === "true");
    Config.setDataValue(path + ":loadCustomFieldsColumns", value === true || value === "true");
  };

  const handleRefreshAutomaticallyChanged = (value) => {
    setRefreshAutomatically(() => value === true || value === "true");
    Config.setDataValue(path + ":refreshAutomatically", value === true || value === "true");
  };

  const handleResizeColumnsAutomaticallyChanged = (value) => {
    setResizeAutomatically(() => value === true || value === "true");
    Config.setDataValue(path + ":resizeColumnsAutomatically", value === true || value === "true");
  };

  const handleUndoButtonClicked = () => {
    setUndoDialogOpen(true);
  }

  const handleUndeleteChangesButtonClicked = () => {
    setUndoDialogOpen(false);
    setUndeletingChanges(true);
  }
  const handleCancelUndoButtonClicked = () => {
    setUndoingChanges(false);
    setUndeletingChanges(false);
    // setUndoDialogOpen(true);
  }
  const handleSubmitUndoButtonClicked = (records) => {
    let undoType = "INVALID";
    if (undeletingChanges) {
      undoType = "UNDELETE";
    }
    setUndoingChanges(false);
    setUndeletingChanges(false);

    let params = {
      undoType: undoType,
      undoKeys: records,
      entityKey: selectedEntity.key,
      entityName: selectedEntity.name
    }
    const submitBody = Utils.createFormBody(params)

    Utils.fetchNonJsonDataUsingNonJson(
      "auditor/submitUndo",
      {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: submitBody
      }).catch((response) => {
      // Need to show error
      Utils.getErrorMessage(response, "Submit undo failed.")
        .then(alert);
      // setUndoDialogOpen(true);
    }).then(() => {
        console.log("navigateToResults")
        navigateToResults();
      }
    );
  }

  const handleInitializeColumnPicker = (p) => {
    columnPicker.current = p;
  };

  const classes = useStyles();

  let ignoredFields = null;
  if (String(ignoredFieldsFilterEnabled) === "true") {
    ignoredFields = Config.fromPath(
      `${path}:ignoredField${selectedEntity.key}`,
      true
    ).data.value;
  }

  return (
    <div>
      <Grid container>
        <Grid item xs={4}>
          <Typography variant="h6" color="textPrimary" display="inline">
            {selectedEntity.displayName} Event Report
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Accordion display="inline" elevation="0" className={classes.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls="options"
              id="options"
              className={classes.expandablePanel}
              display="inline"
            >
              <ExpandMoreIcon
                className={classes.expandablePanelIcon}
              />
              Options
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <StyledBadge
                    badgeContent={
                      <SettingsIcon
                        style={{width: "15px"}}
                        onClick={() => setIgnoredFieldsDialogOpen(!ignoredFieldsDialogOpen)}
                      />
                    }
                  >
                    <ConfigurationSwitch
                      path={path + ":ignoredFieldsFilterEnabled"}
                      handleChanged={setIgnoredFieldsFilterEnabled}
                    />
                  </StyledBadge>
                </Grid>
                <Grid item xs={4}>
                  {!undoUndeleteActive &&
                    <ConfigurationSwitch
                      path={path + ":incrementalDataRetrieval"}
                      handleChanged={handleIncrementalDataRetrievalChanged}
                    />
                  }
                </Grid>
                <Grid item xs={4}/>
                <Grid item xs={4}>
                  <ConfigurationSwitch
                    path={path + ":resizeColumnsAutomatically"}
                    handleChanged={handleResizeColumnsAutomaticallyChanged}
                  />
                </Grid>
                <Grid item xs={8}>
                  <ConfigurationSwitch
                    path={path + ":refreshAutomatically"}
                    handleChanged={handleRefreshAutomaticallyChanged}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ConfigurationSwitch
                    path={path + ":loadCustomFields"}
                    handleChanged={handleLoadCustomFieldsChanged}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ConfigurationSwitch
                    path={path + ":loadCustomFieldsColumns"}
                    handleChanged={handleLoadCustomFieldsColumnsChanged}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <MessageDialog
        handleClose={() => setIgnoredFieldsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        title={"Ignored " + selectedEntity.name + " Events Filter"}
        open={ignoredFieldsDialogOpen}
        description={
          <div>
            <Typography variant="body2" color="textPrimary">
              Filter out unimportant events by providing a list of fields to be
              ignored below.
            </Typography>
            <p/>
            <ConfigurationAsyncComboBox
              fullWidth
              multiple
              path={path + ":ignoredField" + selectedEntity.key}
              url={"auditor/eventFields?name=" + selectedEntity.name}
            />
          </div>
        }
      />

      <MessageDialog
        handleClose={() => setUndoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        title={"Undo/ Undelete Changes"}
        open={undoDialogOpen}
        description={
          <div>
            <Typography variant="body2" color="textPrimary">
              Undo/ undelete changes made by other users. Please select the action you want to perform:
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="stretch">
              <Box display="flex" flexDirection="row" alignItems="center" p={1}>
                <Box p={0.5}>
                  <Button
                    variant="outlined"
                    className={classes.button}
                    onClick={handleUndeleteChangesButtonClicked}
                    style={{width: 100}}
                  >
                    Undelete
                  </Button>
                </Box>
                <Box p={0.5}>
                  <Typography variant="body2" color="textPrimary">
                    Undelete data from the database.
                  </Typography>
                </Box>
              </Box>
            <Typography variant="body2" color="textPrimary">
              After selecting an action to perform, the data  will be filtered to show only relevant events.
            </Typography>
            <Typography variant="body2" color="textPrimary">
              Select the events on which you perform the action and click the [Submit Undo] button. You can also cancel
              the undo operation by clicking the [Cancel Undo] button.
            </Typography>
            </Box>
          </div>
        }
      />

      <div className={classes.columnPickerDiv}>
        <ListBox
          className={classes.columnPicker}
          initialized={handleInitializeColumnPicker}
        />
      </div>

      <AuditorReportTable
        key={selectedEntity.key}
        appSettings={appSettings}
        visibleKey={selectedEntity.key}
        path={path}
        entityName={selectedEntity.name}
        refreshVisible={!refreshAutomatically}
        resizeAutomatically={resizeAutomatically}
        refreshDataAlways={refreshAutomatically}
        loadCustomFields={loadCustomFields}
        loadCustomFieldsColumns={loadCustomFieldsColumns}
        incrementalDataRetrieval={incrementalDataRetrieval && !undoUndeleteActive}
        ignoredFields={ignoredFields}
        useAnyReportServlet={true}
        dateRangeVisible={true}
        updateColumnsOnPageChange={true}
        showUndoButton={selectedEntity.supportsUndoOrUndeletion}
        handleUndoButtonClicked={handleUndoButtonClicked}
        undeletingChanges={undeletingChanges}
        undoUndeleteActive={undoUndeleteActive}
        handleCancelUndoButtonClicked={handleCancelUndoButtonClicked}
        handleSubmitUndoButtonClicked={handleSubmitUndoButtonClicked}
      />
    </div>
  );
};

export default AuditorEventTablePage;
